import React, { forwardRef, useState, useEffect } from 'react';
import { Pressable, View, FlatList, Platform, ScrollView } from 'react-native';
import { Text } from '@constants/Themed';
import toTitleCase from '@hooks/toTitleCase';
import MyInputToolbar from '../../../MessageScreen/Components/InputToolbar';
import GetAvatarImage from '@hooks/getAvatarImage';
import styles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import getDaysAgo from '@utils/getDaysAgo';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import apis, { addNestedComment, deleteComment, likeComment } from '@api/api';
import * as Animatable from 'react-native-animatable';
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { Path, Rect, Svg, Circle } from 'react-native-svg';
import { uniqueId } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { YStack, Image, XStack, Paragraph, SizableText, Button } from 'tamagui';

// Production-ready theme constants
const THEME_COLORS = {
  light: {
    commentBg: '#f8fafc',
    commentBorder: '#e2e8f0',
    connectorLine: 'rgba(59, 130, 246, 0.3)',
    connectorDot: '#3b82f6',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    accent: '#3b82f6',
    hover: '#f1f5f9',
  },
  dark: {
    commentBg: '#1e293b',
    commentBorder: '#334155',
    connectorLine: 'rgba(96, 165, 250, 0.4)',
    connectorDot: '#60a5fa',
    textPrimary: '#f8fafc',
    textSecondary: '#cbd5e1',
    accent: '#60a5fa',
    hover: '#334155',
  }
};

const CommentItem = ({ comment, feedId, removeCallback, initial, updateCallback }) => {
  if (!comment || !comment._id) {
    return null;
  }

  const [showReplies, setShowReplies] = useState(false);
  const [nestedComment, setNestedComment] = useState(comment.replies || []);
  const [showNested, setShowNested] = useState(comment.depth >= 1 ? false : true);
  const theme = themeSelector();
  const user = selectUser();
  const uid = uniqueId();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const colors = THEME_COLORS[theme ? 'light' : 'dark'];
  const depth = comment.depth || 0;
  const indentWidth = Math.min(depth * 16, 64); // Max 64px indent

  // Sync nestedComment with comment.replies prop changes
  useEffect(() => {
    if (comment.replies && Array.isArray(comment.replies)) {
      setNestedComment(comment.replies);
    }
  }, [comment.replies]);

  const _handleAddNestedComment = async (e) => {
    try {
      const response = await addNestedComment(feedId, comment._id, { comment: e, depth: comment.depth });

      // Handle different response structures
      let updatedReplies = null;

      // Case 1: Response has data.comments array (full feed response)
      if (response?.data?.comments && Array.isArray(response.data.comments)) {
        const updatedComment = response.data.comments.find((c: any) => c._id === comment._id);
        if (updatedComment?.replies) {
          updatedReplies = updatedComment.replies;
        }
      }
      // Case 2: Response has data with comments property
      else if (response?.data?.comments) {
        const updatedComment = Array.isArray(response.data.comments)
          ? response.data.comments.find((c: any) => c._id === comment._id)
          : response.data.comments;
        if (updatedComment?.replies) {
          updatedReplies = updatedComment.replies;
        }
      }
      // Case 3: Response data is the new reply object directly
      else if (response?.data?._id) {
        const newReply = response.data;
        updatedReplies = [...(nestedComment || []), newReply];
      }
      // Case 4: Response is the new reply directly
      else if (response?._id) {
        updatedReplies = [...(nestedComment || []), response];
      }

      // Update state if we found new replies
      if (updatedReplies) {
        setNestedComment(updatedReplies);
        // Close the reply input
        setShowReplies(false);
        // Ensure nested replies are visible
        setShowNested(true);
        // Notify parent component to update its comments list
        if (updateCallback) {
          if (response?.data?.comments) {
            updateCallback(response.data.comments);
          } else {
            updateCallback();
          }
        }
      } else {
        // Fallback: refresh from comment prop (will trigger useEffect)
        // This handles cases where the response structure is unexpected
        console.warn('Unexpected response structure from addNestedComment:', response);
      }
    } catch (error) {
      console.error('Error adding nested comment:', error);
    }
  };

  // Improved SVG path - arrow starts from parent comment's avatar and points horizontally to reply's avatar
  const renderSvgPath = (depth) => {
    // Account for padding in XStack (typically 12-16px)
    const padding = Platform.select({ web: 12, default: 8 });
    // Start from parent comment's avatar center (to the left, accounting for indent and padding)
    const startX = -indentWidth - padding + 18;
    const startY = 18; // Avatar center vertically
    // Horizontal line with slight curve down
    const midX = -indentWidth / 2;
    const midY = 30;
    // End at reply's avatar center (accounting for padding)
    const endX = padding + 18;
    const endY = 18;
    // Smooth quadratic curve from parent to reply
    return `M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`;
  };

  // Render arrow head pointing to the reply (right side, at reply's avatar)
  const renderArrowHead = () => {
    const padding = Platform.select({ web: 12, default: 8 });
    const endX = padding + 18;
    const endY = 18;
    return (
      <Path
        d={`M ${endX} ${endY} L ${endX - 6} ${endY - 5} L ${endX - 6} ${endY + 5} Z`}
        fill={colors.connectorDot}
        opacity={0.7}
      />
    );
  };

  return (
    <YStack
      overflow="visible"
      position="relative"
      marginLeft={depth > 0 ? indentWidth : 0}
      marginTop={depth > 0 ? "$2" : 0}
      paddingLeft={depth > 0 ? "$3" : 0}
    >
      {/* --- IMPROVED SVG CONNECTORS --- */}
      {!initial && (
        <YStack
          position="absolute"
          top={0}
          left={-indentWidth - 20}
          zIndex={0}
          pointerEvents="none"
          width={indentWidth + 80}
        >
          <Svg height={50} width={indentWidth + 80} style={{ overflow: "visible" }}>
            <Path
              d={renderSvgPath(depth)}
              stroke={colors.connectorLine}
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {renderArrowHead()}
            {/* Connection dot at start (parent avatar center) */}
            <Circle
              cx={Platform.select({ web: -indentWidth - 12 + 18, default: -indentWidth - 8 + 18 })}
              cy={18}
              r="3.5"
              fill={colors.connectorDot}
              opacity={0.7}
            />
          </Svg>
        </YStack>
      )}

      {/* Vertical line for nested replies - positioned at avatar center */}
      {comment.replies?.length > 0 && showNested && (
        <YStack
          position="absolute"
          left={18}
          top={Platform.select({ web: 50, default: 60 })}
          height="100%"
          zIndex={-1}
          pointerEvents="none"
        >
          <Svg key={uid + 'comment'} style={{ overflow: "visible" }} height="100%">
            <Rect
              x={-1.5}
              y={0}
              width="3"
              height={comment.replies.length <= 1 ? 60 : "90%"}
              fill={colors.connectorLine}
              rx={1.5}
            />
          </Svg>
        </YStack>
      )}

      {/* --- MAIN COMMENT BODY --- */}
      <XStack
        gap="$3"
        paddingHorizontal={Platform.select({ web: "$3", default: "$2" })}
        paddingVertical="$2"
        alignItems="center"
      >
        <Image
          source={{ uri: comment.user?.profile_information?.avatar?.img || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png" }}
          width={36}
          height={36}
          borderRadius={18}
          borderWidth={2}
          borderColor={colors.commentBorder}
          alignSelf="center"
          backgroundColor={colors.commentBg}
        />

        {/* Improved toggle button for nested comments */}
        {comment.replies?.length > 0 && (
          <Pressable
            onPress={() => setShowNested(!showNested)}
            style={{
              padding: 4,
              borderRadius: 12,
              backgroundColor: showNested ? colors.accent + '20' : 'transparent',
              alignSelf: 'center',
            }}
          >
            <MaterialIcons
              name={showNested ? "expand-less" : "expand-more"}
              size={20}
              color={colors.accent}
            />
          </Pressable>
        )}

        <YStack
          flex={1}
          backgroundColor={colors.commentBg}
          borderWidth={1}
          borderColor={colors.commentBorder}
          paddingHorizontal="$3"
          paddingVertical="$2.5"
          borderRadius="$3"
          gap="$1.5"
          shadowColor={colors.textPrimary}
          shadowOffset={{ width: 0, height: 1 }}
          shadowOpacity={0.05}
          shadowRadius={3}
          elevation={1}
        >
          <XStack justifyContent="space-between" alignItems="center" marginBottom="$0.5">
            <SizableText
              size="$3"
              fontWeight="600"
              color={colors.textPrimary}
            >
              {toTitleCase(comment?.user?.profile_information?.name)} {toTitleCase(comment.user?.profile_information?.surname)}
            </SizableText>
            <SizableText
              size="$1"
              color={colors.textSecondary}
            >
              {moment(comment.timestamp).format(globalDate)}
            </SizableText>
          </XStack>
          <Paragraph
            size="$3"
            lineHeight={22}
            color={colors.textPrimary}
          >
            {comment.comment}
          </Paragraph>
        </YStack>
      </XStack>

      {/* --- ACTIONS AND REPLIES SECTION --- */}
      <YStack paddingLeft="$12" marginLeft="$2" marginTop="$1" alignItems="flex-start">
        <XStack gap="$3" alignItems="center" paddingVertical="$1" flexWrap="wrap">
          <SizableText size="$1" color={colors.textSecondary}>
            {getDaysAgo(comment.timestamp)}
          </SizableText>
          <Button
            chromeless
            size="$2"
            onPress={async () => await apis.likeComment(comment._id, feedId)}
            pressStyle={{ opacity: 0.7 }}
            hoverStyle={{ backgroundColor: colors.hover }}
          >
            <XStack gap="$1.5" alignItems="center">
              <FontAwesome name="heart-o" size={14} color={colors.accent} />
              <SizableText size="$2" color={colors.accent}>Like</SizableText>
            </XStack>
          </Button>
          <Button
            chromeless
            size="$2"
            onPress={() => setShowReplies(!showReplies)}
            pressStyle={{ opacity: 0.7 }}
            hoverStyle={{ backgroundColor: colors.hover }}
          >
            <XStack gap="$1.5" alignItems="center">
              <MaterialIcons name="reply" size={14} color={colors.accent} />
              <SizableText size="$2" color={colors.accent}>Reply</SizableText>
            </XStack>
          </Button>
          {comment.user?._id === user.id && (
            <Button
              chromeless
              size="$2"
              onPress={async () => await deleteComment(comment._id, feedId).then(() => removeCallback?.(comment._id))}
              pressStyle={{ opacity: 0.7 }}
              hoverStyle={{ backgroundColor: colors.hover }}
            >
              <XStack gap="$1.5" alignItems="center">
                <MaterialIcons name="delete-outline" size={14} color="#ef4444" />
                <SizableText size="$2" color="#ef4444">Delete</SizableText>
              </XStack>
            </Button>
          )}
        </XStack>

        {comment.replies && comment.replies.length > 0 && showNested && (
          <Animatable.View 
            transition={'height'} 
            duration={300} 
            style={{ 
              alignSelf: "stretch", 
              marginTop: 8,
              paddingLeft: 8,
            }}
          >
            <FlatList
              data={nestedComment}
              renderItem={({ item }) => (
                <CommentItem
                  feedId={feedId}
                  initial={false}
                  comment={item}
                  removeCallback={removeCallback}
                  updateCallback={updateCallback}
                />
              )}
              keyExtractor={(item) => item._id?.toString()}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          </Animatable.View>
        )}

        {showReplies && (
          <Animatable.View
            useNativeDriver
            animation={'fadeInUp'}
            duration={300}
            style={{
              marginTop: 8,
              alignSelf: 'stretch',
            }}
          >
            <XStack
              alignItems="center"
              gap="$2"
              padding="$2.5"
              borderRadius="$3"
              backgroundColor={colors.commentBg}
              borderWidth={1}
              borderColor={colors.commentBorder}
              shadowColor={colors.textPrimary}
              shadowOffset={{ width: 0, height: 2 }}
              shadowOpacity={0.08}
              shadowRadius={4}
              elevation={2}
            >
              <GetAvatarImage
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  borderWidth: 2,
                  borderColor: colors.commentBorder
                }}
              />
              <YStack flex={1}>
                <MyInputToolbar
                  backgroundColor="transparent"
                  sendMessageCallback={(e) => _handleAddNestedComment(e)}
                  forceColumn
                  removeAttach
                  placeHolder={`Reply to ${toTitleCase(comment.user.profile_information?.name)}'s comment`}
                />
              </YStack>
            </XStack>
          </Animatable.View>
        )}
      </YStack>
    </YStack>
  );
};


type CommentsFlatListProps = {
  emptyPlaceHolder?: string;
  comments: any[];
  feedId: string;
  removeCallback?: (id: string) => void;
  updateCallback?: (updatedComments?: any[]) => void;
};

const CommentsFlatList = forwardRef<ScrollView, CommentsFlatListProps>((props, ref) => {
  const { comments, emptyPlaceHolder, feedId, removeCallback, updateCallback } = props;
  const theme = themeSelector();
  const colors = THEME_COLORS[theme ? 'light' : 'dark'];

  if (!comments || comments.length === 0) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 32 }}>
        <YStack alignItems="center" gap="$2" padding="$4">
          <MaterialIcons name="chat" size={48} color={colors.textSecondary} />
          <SizableText
            size="$4"
            color={colors.textSecondary}
            textAlign="center"
          >
            {emptyPlaceHolder || "No comments just yet"}
          </SizableText>
        </YStack>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <FlatList
        data={comments}
        nestedScrollEnabled
        scrollEnabled
        ref={ref as any}
        contentContainerStyle={{
          paddingHorizontal: Platform.select({ default: 12, web: 8 }),
          paddingVertical: 8,
        }}
        ListEmptyComponent={
          <View style={{ flex: 1, minWidth: "100%", paddingVertical: 32, alignItems: 'center' }}>
            <YStack alignItems="center" gap="$2">
              <MaterialIcons name="chat" size={48} color={colors.textSecondary} />
              <SizableText size="$4" color={colors.textSecondary}>
                {emptyPlaceHolder || "No comments found!"}
              </SizableText>
            </YStack>
          </View>
        }
        renderItem={({ item }) => (
          <CommentItem
            initial={true}
            removeCallback={removeCallback}
            feedId={feedId}
            comment={item}
            updateCallback={updateCallback}
          />
        )}
        keyExtractor={(item) => item?._id}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
      />
    </View>
  );
});

export default CommentsFlatList;
